import React, { useEffect, useState } from "react";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserPlus, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["editor", "writer", "viewer"];

export default function Team() {
    const { refresh } = useAuth();
    const [data, setData] = useState({members: [], invites: []});
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("editor");
    const [tokenLimit, setTokenLimit] = useState(50000);
    const [inviteToken, setInviteToken] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/team/members"); setData(data); }
        catch (e) { toast.error(getErrorMessage(e)); }
    };
    useEffect(() => { load(); }, []);

    const invite = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/team/invite", { email, role, token_limit: Number(tokenLimit) || 50000 });
            toast.success(`Invite sent to ${email}`);
            setEmail("");
            setTokenLimit(50000);
            await load();
        } catch (err) { toast.error(getErrorMessage(err)); }
        finally { setLoading(false); }
    };

    const acceptInvite = async (e) => {
        e.preventDefault();
        if (!inviteToken.trim()) return;
        setLoading(true);
        try {
            await api.post("/team/accept-invite", { invite_token: inviteToken.trim() });
            await refresh();
            await load();
            setInviteToken("");
            toast.success("You are now part of the business team.");
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const initials = (n) => (n || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    const colors = ["#FF5B3E", "#3D5BFF", "#2EE8B5", "#FFD23F", "#7B3FE4", "#FF4FA3"];

    return (
        <div data-testid="team-page">
            <PageHeader eyebrow="Team" title="Editors and writers." subtitle="Invite teammates. Set roles. Revoke anytime."/>

            {data.can_manage ? (
                <form onSubmit={invite} data-testid="invite-form" className="bg-white rounded-3xl p-5 border border-cream-300 shadow-soft mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-stretch sm:items-center">
                    <div className="sm:col-span-2 flex items-center gap-2 px-3 bg-cream-100 rounded-2xl border border-cream-300">
                        <Mail size={15} className="text-ink-muted"/>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            data-testid="invite-email" placeholder="teammate@newsroom.com"
                            className="flex-1 bg-transparent py-3 text-[14.5px] focus:outline-none"/>
                    </div>
                    <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role"
                        className="bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] capitalize focus:outline-none focus:ring-2 focus:ring-coral/40">
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input
                        type="number"
                        min="1000"
                        max="5000000"
                        value={tokenLimit}
                        onChange={(e) => setTokenLimit(e.target.value)}
                        className="bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                        placeholder="Token limit"
                    />
                    <button type="submit" disabled={loading} data-testid="invite-submit" className="sm:col-span-4 bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
                        {loading ? <Loader2 size={14} className="animate-spin"/> : <UserPlus size={14}/>} Invite teammate
                    </button>
                </form>
            ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 mb-6 text-[14px] text-amber-900">
                    <p className="font-semibold mb-1">Team management is a Business feature.</p>
                    <p>Everyone can view this page, but only Business plan owners can send invites and manage members.</p>
                </div>
            )}

            {!data.can_manage && (
                <form onSubmit={acceptInvite} className="bg-white rounded-3xl p-5 border border-cream-300 shadow-soft mb-6 flex flex-col sm:flex-row gap-3">
                    <input
                        value={inviteToken}
                        onChange={(e) => setInviteToken(e.target.value)}
                        placeholder="Paste invite token"
                        className="flex-1 bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14.5px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                    />
                    <button type="submit" disabled={loading || !inviteToken.trim()} className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold transition-colors disabled:opacity-70">
                        Accept invite
                    </button>
                </form>
            )}

            <h3 className="font-display text-[22px] font-semibold tracking-tight mb-4">Members ({data.members.length})</h3>
            <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden mb-8">
                <ul className="divide-y divide-cream-300">
                    {data.members.map((m, i) => (
                        <li key={m.user_id} className="flex items-center gap-4 p-5">
                            {m.picture ? <img src={m.picture} alt="" className="w-10 h-10 rounded-full"/> :
                                <div className="w-10 h-10 rounded-full text-white flex items-center justify-center text-[13px] font-bold" style={{background: colors[i % colors.length]}}>{initials(m.name)}</div>}
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-[14px] truncate">{m.name}</p>
                                <p className="text-[12px] text-ink-muted truncate">{m.email}</p>
                            </div>
                            <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold bg-cream-100 text-ink px-2.5 py-1 rounded-full capitalize">{m.role}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {data.invites.length > 0 && (
                <>
                    <h3 className="font-display text-[22px] font-semibold tracking-tight mb-4">Pending invites ({data.invites.length})</h3>
                    <div className="bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                        <ul className="divide-y divide-cream-300">
                            {data.invites.map(inv => (
                                <li key={inv.id} className="flex items-center gap-4 p-5">
                                    <div className="w-10 h-10 rounded-full bg-cream-300 flex items-center justify-center"><Mail size={14}/></div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-[14px] truncate">{inv.email}</p>
                                        <p className="text-[12px] text-ink-muted">Pending · {inv.role} · Limit {Number(inv.token_limit || 0).toLocaleString()} chars</p>
                                        {inv.invite_link && (
                                            <p className="text-[12px] text-coral break-all mt-1">{inv.invite_link}</p>
                                        )}
                                    </div>
                                    <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold bg-sunshine/30 text-ink px-2.5 py-1 rounded-full">Invited</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </div>
    );
}
